import React, { useState, useEffect } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { User } from '../../types/cabinet.types';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface EditAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  onSave: (data: Partial<User>) => Promise<boolean>;
}

export const EditAddressModal: React.FC<EditAddressModalProps> = ({ isOpen, onClose, user, onSave }) => {
  const { t } = useI18n();
  const [street, setStreet] = useState(user?.address?.street || '');
  const [building, setBuilding] = useState(user?.address?.building || user?.address?.house || '');
  const [zip, setZip] = useState(user?.address?.zip || '');
  const [city, setCity] = useState(user?.address?.city || '');
  const [country, setCountry] = useState(user?.address?.country || 'Polska');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{street?: string, city?: string}>({});

  useEffect(() => {
    if (isOpen) {
      setStreet(user?.address?.street || '');
      setBuilding(user?.address?.building || user?.address?.house || '');
      setZip(user?.address?.zip || '');
      setCity(user?.address?.city || '');
      setCountry(user?.address?.country || 'Polska');
      setErrors({});
    }
  }, [isOpen, user]);

  const validate = () => {
    const newErrors: {street?: string, city?: string} = {};
    if (!street || street.trim().length < 3) {
      newErrors.street = t('err_street_invalid') || 'Podaj poprawną nazwę ulicy';
    }
    if (!city || city.trim().length < 2) {
      newErrors.city = t('err_city_invalid') || 'Podaj poprawną nazwę miasta';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    
    setIsSaving(true);
    const success = await onSave({ 
      address: { street, building, zip, city, country } 
    });
    setIsSaving(false);
    if (success) {
      onClose();
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={t('dane_addr')} 
      subtitle={t('edit_your')}
    >
      <div className="cab-field">
        <label>{t('street')}</label>
        <input 
          type="text" 
          className={`cab-input ${errors.street ? 'error' : ''}`} 
          value={street}
          onChange={(e) => {
             setStreet(e.target.value);
             if (errors.street) setErrors({...errors, street: undefined});
          }}
          disabled={isSaving}
        />
        {errors.street && <span className="cab-field-error">{errors.street}</span>}
      </div>

      <div className="cab-field">
        <label>{t('house_number') || 'Numer domu/lokalu'}</label>
        <input 
          type="text" 
          className="cab-input"
          value={building}
          onChange={(e) => setBuilding(e.target.value)}
          disabled={isSaving}
        />
      </div>

      <div className="cab-field">
        <label>{t('zip_code') || 'Kod pocztowy'}</label>
        <input 
          type="text" 
          className="cab-input"
          value={zip}
          placeholder="00-000"
          onChange={(e) => setZip(e.target.value)}
          disabled={isSaving}
        />
      </div>

      <div className="cab-field">
        <label>{t('city')}</label>
        <input 
          type="text" 
          className={`cab-input ${errors.city ? 'error' : ''}`} 
          value={city}
          onChange={(e) => {
             setCity(e.target.value);
             if (errors.city) setErrors({...errors, city: undefined});
          }}
          disabled={isSaving}
        />
        {errors.city && <span className="cab-field-error">{errors.city}</span>}
      </div>
      
      {/* <div className="cab-field" style={{ marginBottom: '24px' }}>
        <label>{t('country')}</label>
        <input 
          type="text" 
          className="cab-input" 
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          disabled={isSaving}
        />
      </div> */}

      <div style={{ display: 'flex', gap: '12px' }}>
        <Button variant="ghost" onClick={onClose} disabled={isSaving} style={{ flex: 1 }}>
          {t('cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave} isLoading={isSaving} style={{ flex: 1 }}>
          {t('save')}
        </Button>
      </div>
    </Modal>
  );
};
